import { useEffect, useMemo, useState } from 'react';
import { agentsApi } from '@/lib/api/agents';
import { useAuthStore, useChatStore, useCoworkerStore } from '@/store';
import { asEntityId } from '@/store/entityId';
import { stripWorkInjectedPrompt } from '@/constants/work';

const EMPTY_CHATS = [];
const EMPTY_TASKS = {};
const PREVIEW_LENGTH = 160;

function timestampOf(value) {
  if (value == null || value === '') return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function messageTimestamp(message) {
  return timestampOf(message?.timestamp ?? message?.date ?? message?.createdAt);
}

function isVisibleMessage(message) {
  return message && (message.role === 'user' || message.role === 'assistant')
    && !message.deletedAt && !message.isHidden && !message.flags?.isHidden
    && ![1, 2, true].includes(message.flags?.isDeleted) && !message.flags?.isArchived
    && message.tool_call_id == null
    && (message.tool_calls == null || (Array.isArray(message.tool_calls) && message.tool_calls.length === 0))
    && !message.params?.hidden && !message.parameters?.hidden
    && ((typeof message.content === 'string' && message.content.trim())
      || message.files?.length > 0 || message.images?.length > 0);
}

function previewText(message, isWork) {
  if (typeof message?.content !== 'string') return null;
  const text = (isWork ? stripWorkInjectedPrompt(message.content) : message.content)
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]*>/g, '')
    .replace(/[*_`#~]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return null;
  return text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH - 1)}…` : text;
}

function lastVisibleMessage(chat) {
  let latest = null;
  for (const message of Array.isArray(chat.messages) ? chat.messages : EMPTY_CHATS) {
    if (!isVisibleMessage(message)) continue;
    if (!latest || messageTimestamp(message) == null || messageTimestamp(latest) == null
      || messageTimestamp(message) >= messageTimestamp(latest)) latest = message;
  }
  const summary = chat.lastMessage;
  if (isVisibleMessage(summary)
    && (!latest || (messageTimestamp(summary) ?? -Infinity) > (messageTimestamp(latest) ?? -Infinity))) {
    latest = summary;
  }
  return latest;
}

function isExcluded(record) {
  return record?.deletedAt || record?.isArchived || record?.flags?.isArchived
    || record?.isTemporary || record?.flags?.isTemporary
    || record?.visibility === 'public' || record?.isPlusLocked
    || ((record?.flags?.isToolRun || record?.tool) && !record?.flags?.isToolPromotedToChat);
}

/** Only explicit thread associations count; message agent mentions never establish ownership. */
export function buildBotConversationPreviews({ chats = EMPTY_CHATS, tasks = EMPTY_TASKS, userId } = {}) {
  const ownerId = asEntityId(userId);
  const previewsByBotId = {};
  const selectionFreshness = new Map();
  if (!ownerId) return previewsByBotId;

  const tasksByConversation = new Map();
  const tasksById = new Map();
  for (const task of Object.values(tasks || EMPTY_TASKS)) {
    if (!task || isExcluded(task)) continue;
    const taskOwner = asEntityId(task.user ?? task.userId);
    if (taskOwner && taskOwner !== ownerId) continue;
    const id = asEntityId(task.id ?? task._id);
    if (id) tasksById.set(id, task);
    const conversationId = asEntityId(task.conversationId ?? task.conversation);
    if (conversationId) tasksByConversation.set(conversationId, task);
  }

  for (const chat of chats || EMPTY_CHATS) {
    if (!chat || isExcluded(chat) || asEntityId(chat.user) !== ownerId || chat.isOwner === false) continue;
    const conversationId = asEntityId(chat.id ?? chat._id);
    if (!conversationId || /^(?:temp|temporary|local)[-_:]/i.test(conversationId)) continue;
    const sessionId = asEntityId(chat.coworkerSession);
    const linkedTask = tasksById.get(sessionId) || tasksByConversation.get(conversationId);
    const taskConversationId = asEntityId(linkedTask?.conversationId ?? linkedTask?.conversation);
    const task = linkedTask && (!taskConversationId || taskConversationId === conversationId) ? linkedTask : null;
    const associations = new Set([
      asEntityId(chat.agent),
      asEntityId(chat.agentId),
      asEntityId(task?.agentId),
      asEntityId(task?.context?.agentId),
    ].filter(Boolean));
    // Conflicting authoritative associations are not a one-to-one bot thread.
    if (associations.size !== 1) continue;
    const [botId] = associations;
    const message = lastVisibleMessage(chat);
    const timestamp = messageTimestamp(message);
    const freshness = timestamp ?? timestampOf(chat.updatedAt) ?? timestampOf(chat.createdAt);
    const entry = {
      conversationId,
      preview: previewText(message, !!(sessionId || task || chat.flags?.isCoworkerSession)),
      timestamp,
    };
    const previous = Object.prototype.hasOwnProperty.call(previewsByBotId, botId) ? previewsByBotId[botId] : null;
    if (!previous || (freshness ?? -Infinity) > (selectionFreshness.get(botId) ?? -Infinity)) {
      selectionFreshness.set(botId, freshness);
      Object.defineProperty(previewsByBotId, botId, { value: entry, enumerable: true, configurable: true });
    }
  }
  return previewsByBotId;
}

function mergeConversationPreviews({ server, chats, tasks, userId, requestedIds }) {
  if (!userId) return {};
  const byConversation = new Map((chats || EMPTY_CHATS).map(chat => [asEntityId(chat.id), chat]));
  const serverEntries = new Map();
  const associations = new Map();
  for (const botId of requestedIds) {
    const entry = Object.prototype.hasOwnProperty.call(server, botId) ? server[botId] : null;
    const conversationId = asEntityId(entry?.conversationId);
    if (!conversationId) continue;
    const cached = byConversation.get(conversationId);
    if (cached && (isExcluded(cached) || cached.isOwner === false
      || (asEntityId(cached.user) && asEntityId(cached.user) !== userId))) continue;
    serverEntries.set(botId, {
      conversationId,
      preview: previewText({ content: entry.preview }, false),
      timestamp: timestampOf(entry.timestamp),
    });
    associations.set(conversationId, botId);
  }
  // The owner-scoped endpoint is authoritative even when the local list omitted agent.
  const associatedChats = (chats || EMPTY_CHATS).map(chat => {
    const botId = associations.get(asEntityId(chat.id));
    return botId && !chat.agent && !chat.agentId ? { ...chat, agentId: botId } : chat;
  });
  const local = buildBotConversationPreviews({ chats: associatedChats, tasks, userId });
  const merged = Object.fromEntries(serverEntries);
  for (const [botId, entry] of Object.entries(local)) {
    if (requestedIds.size && !requestedIds.has(botId)) continue;
    const remote = serverEntries.get(botId);
    const cachedMessage = lastVisibleMessage(byConversation.get(entry.conversationId) || {});
    const hasMessageDate = messageTimestamp(cachedMessage) !== null;
    const isNewer = hasMessageDate && (entry.timestamp ?? -Infinity) > (remote?.timestamp ?? -Infinity);
    const sameMessage = hasMessageDate && remote?.conversationId === entry.conversationId && entry.timestamp === remote.timestamp;
    if (!remote || isNewer || (sameMessage && entry.preview !== null)) {
      Object.defineProperty(merged, botId, { value: entry, enumerable: true, configurable: true });
    }
  }
  return merged;
}

/** Missing entries remain unknown history; this endpoint only covers explicit associations. */
export default function useBotConversationPreviews(botIds = EMPTY_CHATS) {
  const userId = useAuthStore(state => asEntityId(state.user?.id ?? state.user?._id));
  const token = useAuthStore(state => state.token);
  const chats = useChatStore(state => state.chats);
  const tasks = useCoworkerStore(state => state.tasks);
  const idsKey = JSON.stringify([...new Set(botIds.flatMap(value => {
    const id = asEntityId(value).trim();
    return id ? [id] : [];
  }))].sort().slice(0, 100));
  const [response, setResponse] = useState(null);

  useEffect(() => {
    setResponse(null);
    const ids = JSON.parse(idsKey);
    if (!token || !userId || !ids.length) return undefined;
    let active = true;
    agentsApi.getConversationPreviews(ids).then(data => {
      if (active) setResponse({ userId, token, idsKey, previews: data?.previewsByBotId || {} });
    }).catch(() => {
      // Older deployments can return 404; an unavailable preview is not empty history.
      if (active) setResponse({ userId, token, idsKey, previews: {} });
    });
    return () => { active = false; };
  }, [idsKey, token, userId]);

  const previewsByBotId = useMemo(() => {
    const currentResponse = response?.userId === userId && response?.token === token && response?.idsKey === idsKey;
    return mergeConversationPreviews({
      server: currentResponse ? response.previews : EMPTY_TASKS,
      chats, tasks, userId: token ? userId : null, requestedIds: new Set(JSON.parse(idsKey)),
    });
  }, [chats, tasks, token, userId, idsKey, response]);

  return { previewsByBotId, hasCompleteHistory: false };
}
